import { useCallback, useMemo } from 'react';
import type { SelectOptionList } from '@cbhq/cds-web/alpha/select';
import { Select } from '@cbhq/cds-web/alpha/select';
import { Switch } from '@cbhq/cds-web/controls/Switch';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { Text } from '@cbhq/cds-web/typography/Text';
import { defineMessages, useIntl } from '@cbhq/i18n';

export const RULE_NAME_COL_WIDTH = 280;
export const APPROVER_COL_WIDTH = 280;
export const REQUIREMENT_COL_WIDTH = 120;
export const SELECT_CONTROL_HEIGHT = 48;
export const APPROVAL_RULE_ROW_GAP = 4;

const messages = defineMessages({
  emptyPlaceholder: {
    id: 'Security.approvalRuleRow.emptyPlaceholder',
    defaultMessage: '-',
    description:
      'Placeholder shown in the approver and requirement dropdowns when no value is configured',
  },
  toggleAccessibilityLabel: {
    id: 'Security.approvalRuleRow.toggleAccessibilityLabel',
    defaultMessage: 'Enable approval requirement for {ruleName}',
    description: 'Accessibility label for the switch that enables/disables an approval rule row',
  },
  approverAccessibilityLabel: {
    id: 'Security.approvalRuleRow.approverAccessibilityLabel',
    defaultMessage: 'Approvers for {ruleName}',
    description: 'Accessibility label for the approver dropdown in an approval rule row',
  },
  requirementCountAccessibilityLabel: {
    id: 'Security.approvalRuleRow.requirementCountAccessibilityLabel',
    defaultMessage: 'Required approvals for {ruleName}',
    description:
      'Accessibility label for the required approval count dropdown in an approval rule row',
  },
});

const APPROVER_SELECT_STYLES = {
  root: { width: APPROVER_COL_WIDTH },
  controlInputNode: { minHeight: SELECT_CONTROL_HEIGHT, height: SELECT_CONTROL_HEIGHT },
  controlValueNode: { flexWrap: 'nowrap', overflow: 'hidden' },
  dropdown: {
    padding: 7,
  },
  optionContent: {
    paddingTop: 2,
    paddingBottom: 2,
  },
} as const;

const COUNT_SELECT_STYLES = {
  root: { width: REQUIREMENT_COL_WIDTH },
  controlInputNode: { minHeight: SELECT_CONTROL_HEIGHT, height: SELECT_CONTROL_HEIGHT },
  dropdown: {
    padding: 7,
    width: 'max-content',
  },
  optionContent: {
    paddingTop: 2,
    paddingBottom: 2,
  },
} as const;

const SELECT_CLASS_NAMES = {
  dropdown: 'workspaceSelectHiddenScrollbar',
} as const;

export type ApprovalRuleRowProps = {
  ruleName: string;
  maxRequiredApprovals: number;
  isEnabled: boolean;
  approvers: string[];
  requirementCount: number;
  approverOptions: SelectOptionList<'multi', string>;
  disabled?: boolean;
  onToggle: () => void;
  onApproversChange: (value: string | string[] | null) => void;
  onCountChange: (count: number) => void;
};

export function ApprovalRuleRow({
  ruleName,
  maxRequiredApprovals,
  isEnabled,
  approvers,
  requirementCount,
  approverOptions,
  disabled = false,
  onToggle,
  onApproversChange,
  onCountChange,
}: ApprovalRuleRowProps) {
  const { formatMessage } = useIntl();

  const countOptions = useMemo<SelectOptionList<'single', string>>(
    () =>
      Array.from({ length: maxRequiredApprovals }, (_, index) => {
        const count = index + 1;
        return { value: String(count), label: String(count) };
      }),
    [maxRequiredApprovals],
  );

  const handleCountChange = useCallback(
    (value: string | null) => {
      if (value == null) return;
      onCountChange(Number(value));
    },
    [onCountChange],
  );

  return (
    <HStack alignItems="center" gap={APPROVAL_RULE_ROW_GAP} width="100%">
      <Box width={RULE_NAME_COL_WIDTH}>
        <Text font="label1">{ruleName}</Text>
      </Box>

      <Box width={APPROVER_COL_WIDTH}>
        <Select<'multi', string>
          type="multi"
          compact
          hideSelectAll
          maxSelectedOptionsToShow={1}
          classNames={SELECT_CLASS_NAMES}
          styles={APPROVER_SELECT_STYLES}
          value={approvers}
          onChange={onApproversChange}
          options={approverOptions}
          placeholder={formatMessage(messages.emptyPlaceholder)}
          controlAccessibilityLabel={formatMessage(messages.approverAccessibilityLabel, {
            ruleName,
          })}
          disabled={disabled || !isEnabled}
        />
      </Box>

      <Box width={REQUIREMENT_COL_WIDTH}>
        <Select<'single', string>
          type="single"
          compact
          classNames={SELECT_CLASS_NAMES}
          styles={COUNT_SELECT_STYLES}
          value={requirementCount > 0 ? String(requirementCount) : null}
          onChange={handleCountChange}
          options={countOptions}
          placeholder={formatMessage(messages.emptyPlaceholder)}
          controlAccessibilityLabel={formatMessage(messages.requirementCountAccessibilityLabel, {
            ruleName,
          })}
          disabled={disabled || !isEnabled || maxRequiredApprovals === 0}
        />
      </Box>

      <HStack flexGrow={1} justifyContent="flex-end">
        <Switch
          checked={isEnabled}
          onChange={onToggle}
          disabled={disabled}
          accessibilityLabel={formatMessage(messages.toggleAccessibilityLabel, { ruleName })}
        />
      </HStack>
    </HStack>
  );
}
